import type { ResourceType, StatType } from '../types';

export const ResourceIcons: Record<ResourceType, string> = {
  money: '💰',
  food: '🍞',
  motivation: '⚡',
  memory: '💭',
  connections: '🤝',
  health: '❤️',
  bad_habit: '⚠️',
  sanity: '🧠',
  dignity: '⭐',
};

export const StatIcons: Record<StatType, string> = {
  smarts: '📚',
  tenacity: '👁️',
  social: '💬',
};

export function getResourceIcon(resource: ResourceType): string {
  return ResourceIcons[resource] || '';
}

export function getStatIcon(stat: StatType): string {
  return StatIcons[stat] || '';
}

export function getResourceLabel(resource: ResourceType): string {
  const labels: Record<ResourceType, string> = {
    money: 'Money',
    food: 'Food',
    motivation: 'Motivation',
    memory: 'Memory',
    connections: 'Connections',
    health: 'Health',
    bad_habit: 'Bad Habits',
    sanity: 'Sanity',
    dignity: 'Dignity',
  };
  return labels[resource] || resource;
}

export function getStatLabel(stat: StatType): string {
  const labels: Record<StatType, string> = {
    smarts: 'Smarts',
    tenacity: 'Tenacity',
    social: 'Social',
  };
  return labels[stat] || stat;
}

// Replace resource and stat names in text with their symbols
export function replaceResourceNamesWithSymbols(text: string): string {
  let result = text;
  
  // Replace resources - handle singular and plural forms
  const resourceReplacements: Array<[string, ResourceType]> = [
    ['Money', 'money'],
    ['Food', 'food'],
    ['Motivation', 'motivation'],
    ['Memory', 'memory'],
    ['Connections', 'connections'],
    ['Connection', 'connections'], // Handle singular form
    ['Health', 'health'],
    ['Bad Habits', 'bad_habit'],
    ['Sanity', 'sanity'],
    ['Dignity', 'dignity'],
  ];
  
  for (const [name, resource] of resourceReplacements) {
    const icon = getResourceIcon(resource);
    // Check if the icon already exists after the resource name
    // Pattern: "ResourceName" followed by optional whitespace and the icon
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedIcon = icon.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const alreadyHasIconPattern = new RegExp(`${escapedName}\\s*${escapedIcon}`);
    
    // Only process if icon is not already present anywhere in the text
    if (!alreadyHasIconPattern.test(result)) {
      // Replace "Resource" with "Resource icon" only if icon is not already present
      // Use negative lookahead to check if icon already follows the resource name
      const regex = new RegExp(`\\b${escapedName}\\b(?!\\s*${escapedIcon})`, 'g');
      result = result.replace(regex, `${name} ${icon}`);
    }
  }
  
  // Replace stats
  const statReplacements: Array<[string, StatType]> = [
    ['Smarts', 'smarts'],
    ['Tenacity', 'tenacity'],
    ['Social', 'social'],
  ];
  
  for (const [name, stat] of statReplacements) {
    const icon = getStatIcon(stat);
    // Check if the icon already exists
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedIcon = icon.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const alreadyHasIconPattern = new RegExp(`${escapedName}\\s*${escapedIcon}`);
    
    if (!alreadyHasIconPattern.test(result)) {
      const regex = new RegExp(`\\b${escapedName}\\b(?!\\s*${escapedIcon})`, 'g');
      result = result.replace(regex, `${name} ${icon}`);
    }
  }
  
  return result;
}

